import React from 'react';
import { Button } from '@/components/shared/ui';
import { ChevronLeft, Home } from 'lucide-react';

export interface FullScreenHeaderProps {
    /** 헤더 타이틀 (문자열 또는 클래스명+평가결과 등 React 노드) */
    title: React.ReactNode;
    onBack?: () => void;
    /** 마스터 등: 뒤로가기 대신 홈 버튼 표시 (클릭 시 이 콜백 호출, 예: 랜딩 이동) */
    onHome?: () => void;
    rightContent?: React.ReactNode;
    className?: string;
    /** onHome 사용 시 홈 버튼에 표시할 로고 이미지 URL */
    homeLogo?: string;
    /** 로고 이미지 URL. 없으면 logoFallbackText로 텍스트 플레이스홀더 표시 */
    logo?: string;
    /** 로고가 없을 때 표시할 텍스트 (예: 기관명). h-10, bg-brand-500, text-white */
    logoFallbackText?: string;
    /** onHome 사용 시 버튼에 표시할 텍스트 */
    homeLabel?: string;
}

/**
 * FullScreenHeader - FullScreenLayout용 표준 헤더 컴포넌트
 * 
 * 공통 헤더 구조:
 * - 좌측: 뒤로가기 버튼 + 구분선 + 타이틀
 * - 우측: 커스텀 컨텐츠
 */
export const FullScreenHeader: React.FC<FullScreenHeaderProps> = ({
    title,
    onBack,
    onHome,
    rightContent,
    className = '',
    homeLogo,
    logo,
    logoFallbackText,
    homeLabel = '홈으로',
}) => {
    const showLogoArea = logo != null || (logoFallbackText != null && logoFallbackText !== '');
    return (
        <div className={`w-full mx-auto pl-4 pr-8 h-16 flex items-center justify-between ${className}`}>
            <div className="flex items-center space-x-4">
                {onHome && (
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onHome}
                            className={homeLogo ? 'h-11 px-2' : 'h-11 gap-1.5'}
                            aria-label={homeLabel}
                        >
                            {homeLogo ? (
                                <img src={homeLogo} alt="" className="h-6 w-auto" />
                            ) : (
                                <>
                                    <Home size={20} />
                                    {homeLabel && <span className="text-sm font-medium">{homeLabel}</span>}
                                </>
                            )}
                        </Button>
                        <div className="h-6 w-px bg-slate-200 mx-2"></div>
                    </>
                )}
                {!onHome && onBack && (
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            className="h-11 w-11"
                        >
                            <ChevronLeft size={20} />
                        </Button>
                        <div className="h-6 w-px bg-slate-200 mx-2"></div>
                    </>
                )}
                {showLogoArea && (
                    <div className="h-10 w-fit min-w-[2.5rem] rounded-lg overflow-hidden shrink-0 flex items-center justify-center">
                        {logo ? (
                            <img src={logo} alt="" className="h-full w-auto max-w-[12rem] object-contain" />
                        ) : (
                            <span className="h-full px-3 flex items-center justify-center bg-brand-500 text-white text-sm font-medium whitespace-nowrap">
                                {logoFallbackText}
                            </span>
                        )}
                    </div>
                )}
                <h1 className="font-semibold text-slate-900 leading-tight">
                    {title}
                </h1>
            </div>

            {rightContent && (
                <div className="flex items-center gap-4">
                    {rightContent}
                </div>
            )}
        </div>
    );
};
